import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { CustomerService, DocketService } from '../../shared/';
import { Customer, User } from '../../models/';
import { ConfigService } from '../../shared/services/config.service';
import { BookingService } from '../../shared/services/booking.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { NewTransactionDialogComponent } from '../../shared/dialogs/new-transaction-dialog/new-transaction-dialog.component';
import { MergeCustomerDialogComponent } from './merge-customer-dialog/merge-customer-dialog.component';
import { EditCustomerAlertDialogComponent } from '../edit-customer-alert-dialog/edit-customer-alert-dialog.component';
import { CustomerAlertDialogComponent } from '../../shared/dialogs/customer-alert-dialog/customer-alert-dialog.component';
import { CUSTOMER } from 'src/testIds';
import { Subject } from 'rxjs';
import { CustomerNamePipe } from 'src/app/shared/pipes/customer-name.pipe';
import { AsyncPipe, CurrencyPipe, NgFor, NgIf } from '@angular/common';
import { IntegrationRelationshipInfoComponent } from 'src/app/shared/integration-relationship-info/integration-relationship-info.component';
import { CustomerContactDetailsPipe } from 'src/app/shared/pipes/customer-contact-details.pipe';
import { CustomerAddressPipe } from 'src/app/shared/pipes/customer-address.pipe';
import { KeyValuePairComponent } from 'src/app/shared/general/key-value-pair/key-value-pair.component';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { LocationNameFromIdPipe } from 'src/app/shared/pipes/location-name-from-id.pipe';
import { UserIdToNamePipe } from 'src/app/shared/pipes/user-id-to-name.pipe';
import { MatTabsModule } from '@angular/material/tabs';
import { CustomersPricesTabComponent } from './prices-tab/customers-prices-tab.component';
import { HlCurrencyPipe } from 'src/app/shared/pipes/hl-currency.pipe';
import { BookingListComponent } from 'src/app/shared/bookings/bookings-list/booking-list.component';
import { CustomerEquipmentComponent } from './customer-equipment/customer-equipment.component';
import { SalesHistoryComponent } from './sales-history/sales-history.component';
import { CommentsComponent } from 'src/app/shared/comments/comments.component';
import { MomentModule } from 'ngx-moment';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { EmailListComponent } from 'src/app/shared/email-list/email-list.component';
import { PreviouslyHiredItemsComponent } from './previously-hired-items/previously-hired-items.component';
import { BookingItemNamePipe } from 'src/app/shared/pipes/booking-item-name.pipe';

@Component({
  selector: 'customer',
  templateUrl: './customer.component.html',
  styleUrls: ['./customer.component.css'],
  providers: [
    CustomerService,
    DocketService,
    BookingService,
    BookingItemNamePipe
  ],
  standalone: true,
  imports: [
    CustomerNamePipe,
    NgIf,
    IntegrationRelationshipInfoComponent,
    CustomerContactDetailsPipe,
    CustomerAddressPipe,
    KeyValuePairComponent,
    RouterLink,
    MatMenu,
    MatMenuItem,
    TestIdDirective,
    AsyncPipe,
    MatTabsModule,
    CustomerNamePipe,
    CustomersPricesTabComponent,
    HlCurrencyPipe,
    BookingListComponent,
    CustomerEquipmentComponent,
    SalesHistoryComponent,
    CommentsComponent,
    MomentModule,
    MatIcon,
    MatButton,
    MatMenuTrigger,
    EmailListComponent,
    PreviouslyHiredItemsComponent
  ]
})
export class CustomerComponent implements OnInit, OnDestroy {
  customer: Customer;
  testIds = CUSTOMER;
  fullName: string;
  newComment: any = {
    commentable_type: 'customer'
  };
  currentUser: User;
  height = 400;
  dateTimeFormat = ConfigService.dates.longDateTimeFormat;
  currency = 'GBP';
  private ngUnsubscribe = new Subject<void>();
  customerBalances = null;
  emailListSource$ = this.route.params.pipe(
    map((params) => ({
      resource: 'customer',
      id: Number(params['customerId'])
    }))
  );

  customerId$ = this.route.params.pipe(
    map((params) => Number(params['customerId']))
  );

  constructor(
    private route: ActivatedRoute,
    private customerService: CustomerService,
    private configService: ConfigService,
    private toastService: ToastrService,
    private dialog: MatDialog,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit() {
    this.configService.currentUser.subscribe((user) => {
      if (!user) {
        return;
      }
      this.currentUser = user;
      this.newComment.user_id = user.id;
    });
    this.configService.userConfig
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((config) => {
        if ('currency' in config) {
          this.currency = config['currency'];
        }
      });
    this.customerId$
      .pipe(
        mergeMap((id) => this.getCustomer(id)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (response) => {
          this.newComment.commentable_id = response.id;
          this.customer = response;
          this.getCustomerBalances(response.id);
          this.getFullName();
          this.checkForDisabled();
          this.checkForCustomerAlert();
        },
        (error) => {}
      );
  }

  checkForDisabled() {
    if (this.customer.hires_disabled === 1) {
      this.toastService.warning(
        'Hires for this customer are currently disabled, to re-enable, go to edit customer',
        'Disabled'
      );
    }
  }

  newDocket() {
    this.checkForDisabled();
    if (this.customer.hires_disabled === 1) {
      return;
    }
    this.dialog.open(NewTransactionDialogComponent, {
      data: {
        customer: this.customer
      }
    });
  }

  mergeCustomer() {
    this.dialog.open(MergeCustomerDialogComponent, {
      data: { customer: this.customer }
    });
  }

  getFullName() {
    this.fullName = this.customerService.getName(this.customer);
  }

  editAlertMessage() {
    this.dialog.open(EditCustomerAlertDialogComponent, {
      data: this.customer,
      viewContainerRef: this.viewContainerRef
    });
  }

  private checkForCustomerAlert() {
    if (this.customer.popup_message) {
      this.dialog.open(CustomerAlertDialogComponent, {
        data: this.customer
      });
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getCustomer(id: number) {
    return this.customerService
      .getCustomer(id, [
        'dockets.items.item.product',
        'comments',
        'bookings.items.item',
        'bookings.items.product',
        'bookings.items.category'
      ])
      .pipe(
        map((customer) => {
          customer.bookings = customer.bookings.map((booking) => {
            booking.items = booking.items.filter(
              (bookingItem) => !bookingItem.deleted_at
            );
            return booking;
          });
          return customer;
        })
      );
  }

  private getCustomerBalances(id: number) {
    this.customerService.getBalances(id).subscribe((response) => {
      this.customerBalances = response;
    });
  }
}
